/** Create or edit a tracking campaign. */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { changed } from '../../lib/urltracking.js';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';
import { CAMPAIGN_STATUS } from '../../constants/urltracking.js';

const BLANK = {
  name: '', description: '', start_date: '', end_date: '', status: 'Draft', notes: ''
};

export default function TrackCampaignSheet({ id, onClose }) {
  const { toast } = useApp();
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.urlTracking.campaigns.get(id)
      .then(row => setForm({
        name: row.name || '',
        description: row.description || '',
        /* DATE columns arrive as 'YYYY-MM-DD', which is exactly what a date
           input wants; anything else would leave the field blank. */
        start_date: (row.start_date || '').slice(0, 10),
        end_date: (row.end_date || '').slice(0, 10),
        status: row.status || 'Draft',
        notes: row.notes || ''
      }))
      .catch(e => toast(e.message))
      .finally(() => setLoading(false));
  }, [id, toast]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!String(form.name).trim()) { toast('Name the campaign'); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast('The end date is before the start date');
      return;
    }

    setSaving(true);
    try {
      if (id) await api.urlTracking.campaigns.update(id, form);
      else await api.urlTracking.campaigns.create(form);
      changed();
      toast(id ? 'Campaign saved' : 'Campaign created');
      onClose();
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      icon="mega"
      tone="g-pink"
      title={id ? 'Edit campaign' : 'New tracking campaign'}
      onClose={onClose}
      onSave={loading ? undefined : save}
      saveLabel={saving ? 'Saving…' : 'Save campaign'}
    >
      {loading ? <p className="muted">Loading…</p> : (
        <>
          <Group>Campaign</Group>

          <Field label="Name" wide>
            <input
              autoFocus
              value={form.name}
              placeholder="Diwali Offer 2026"
              onChange={e => set('name', e.target.value)}
            />
          </Field>

          <Field label="Description" wide>
            <input
              value={form.description}
              placeholder="20% discount campaign"
              onChange={e => set('description', e.target.value)}
            />
          </Field>

          <Field label="Starts">
            <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </Field>
          <Field label="Ends">
            <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </Field>

          <Field
            label="Status"
            hint={form.status === 'Archived'
              ? <small className="utbad">Archiving stops every link in this campaign redirecting.</small>
              : null}
          >
            <OptionSelect value={form.status} onChange={v => set('status', v)} options={CAMPAIGN_STATUS} />
          </Field>

          <Group>Notes</Group>
          <Field label="Notes" wide>
            <textarea
              rows={4}
              value={form.notes}
              placeholder="Anything the team should know about this push."
              onChange={e => set('notes', e.target.value)}
            />
          </Field>

          <p className="utnote">
            The dates are the campaign&apos;s own window and are recorded for reference. They do not
            switch its links on or off — a link redirects while it is Active and stops when it is not,
            which keeps that decision somewhere you can see it.
          </p>
        </>
      )}
    </Sheet>
  );
}
